import moment from "moment";
import {
  useEffect, useState, useContext, useMemo,
} from "react";
import { useForm, useWatch, FormProvider } from "react-hook-form";
import MainContext from "../../context/main/mainContext";
import CollapsibleSection from "../../components/CollapsibleSection";
import HeaderBar from "./HeaderBar";
import CompanyInfo from "./CompanyInfo";
import BillingDetails from "./BillingDetails";
import SeatsConfig from "./SeatsConfig";
import EnrichmentConfig from "./EnrichmentConfig";
import DedicatedCsmConfig from "./DedicatedCsmConfig";
import PreviewSidebar from "./PreviewSideBar";
import ActionsBar from "./ActionBar";
import StripeLinks from "./StripeLinks";
import money from "../../utilities/money";
import { getPercentOffInfo } from "../../utilities/percentOff";
import InfoBadge from "../../components/InfoBadge";

const initialForm = {
  companyName: "",
  billing: {
    customMemo: "",
    interval: "year",
    intervalCount: 1,
    intervalOption: "yearly",
    chargeAutomatically: false,
    enableTaxExemption: false,
    cycleAnchorOn: new Date().toISOString().slice(0, 10),
    subscriptionStartOn: new Date().toISOString().slice(0, 10),
    invoiceDueOn: moment().add(1, "month").toISOString().slice(0, 10),
    resetCycle: false,
    address: {
      line: "",
      line2: "",
      city: "",
      state: "",
      zipcode: "",
      country: "US",
    },
    trial: {
      enabled: false,
      expiresOn: moment().add(14, "days").toISOString().slice(0, 10),
    },
    contacts: [],
  },
  seats: {
    paid: 1,
    freeSeatsEnabled: true,
    free: 150,
    dollars: 48,
    hasCustomPricing: false,
  },
  subscriptionTier: "",
  enrichment: {
    enabled: false,
    credits: 500,
    dollars: 12,
    hasCustomPricing: false,
    level: "PREMIUM",
    options: {
      hasListEnrichment: true,
      hasFreeListEnrichment: false,
      hasEventIntelligence: false,
    },
  },
  dedicatedCsm: {
    enabled: false,
    hasCustomPricing: false,
    dollars: 1000,
  },
};

export default function TeamSubscriptionForm({
  onOpenStripePaymentModal: handleOpenStripePaymentModal,
}) {
  const mainContext = useContext(MainContext);
  const team = mainContext?.team?.team || null;
  const {
    stripe_customer: stripeCustomer,
    previewSubscription,
    createOrUpdateTeamSubscription,
    getSubscriptionFormPrefillForTeam,
  } = mainContext || {};

  const isUpdateMode = Boolean(team?.stripeSubscription);

  const methods = useForm({
    defaultValues: initialForm,
    mode: "onSubmit",
    reValidateMode: "onChange",
  });
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = methods;

  const form = useWatch({ control });

  useEffect(() => {
    (async () => {
      if (
        !(
          stripeCustomer?.subscriptions?.data?.length
          || internalDemoTeam?.hasPaid
          || internalDemoTeam?.isDeleted
          || internalDemoTeam?.isExpired
        )
      ) return;
      const data = await getSubscriptionFormPrefillForTeam(team.id);
      reset(data);
    })();
  }, [team?.id, stripeCustomer?.id, stripeCustomer?.subscriptions, reset]);

  const [isLoading, setIsLoading] = useState(false);
  const [previewInfo, setPreviewInfo] = useState({});
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);

  const [sectionsExpanded, setSectionsExpanded] = useState({
    company: !isUpdateMode,
    billing: !isUpdateMode,
    seats: !isUpdateMode,
    enrichment: !isUpdateMode,
    dedicatedCsm: !isUpdateMode,
  });

  const internalDemoTeam = useMemo(
    () => team?.internalDemoTeams.find((team) => !team.isDeleted && !team.isExpired && !team.hasPaid),
    [team],
  );

  const [generatedInvoice, setGeneratedInvoice] = useState({});

  const toggleSection = (section) => {
    setSectionsExpanded((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const payload = useMemo(() => {
    const f = form || {};
    return {
      companyName: f.companyName,
      billing: {
        ...(f.billing.trial
          && f.billing.trial.enabled && { trialExpiresOn: f.billing.trial.expiresOn || undefined }),
        ...(f.billing?.customMemo && { customMemo: f.billing.customMemo }),
        interval: f.billing?.interval,
        intervalCount: Number(f.billing?.intervalCount) || 1,
        chargeAutomatically: Boolean(f.billing?.chargeAutomatically),
        enableTaxExemption: Boolean(f.billing?.enableTaxExemption),
        ...(!f.billing?.chargeAutomatically && {
          invoiceDueOn: f.billing?.invoiceDueOn || undefined,
        }),
        ...(f.billing?.subscriptionStartOn && {
          subscriptionStartOn: f.billing.subscriptionStartOn,
        }),
        ...(f.billing?.cycleAnchorOn && { cycleAnchorOn: f.billing.cycleAnchorOn }),
        resetCycle: Boolean(f.billing?.resetCycle),
        address: {
          line: f.billing?.address?.line,
          ...(f.billing?.address?.line2 && { line2: f.billing.address.line2 }),
          city: f.billing?.address?.city,
          state: f.billing?.address?.state,
          zipcode: f.billing?.address?.zipcode,
          country: f.billing?.address?.country,
        },
      },
      seats: {
        paid: Number(f.seats?.paid) || 0,
        ...(f.seats?.freeSeatsEnabled ? { free: Number(f.seats?.free ?? 0) } : undefined),
        ...(f.seats?.hasCustomPricing && { unitAmount: money.dollarsToCents(f.seats.dollars) }),
      },
      subscriptionTier: f.subscriptionTier || "",
      ...(f.enrichment
        && f.enrichment.enabled && {
        enrichment: {
          credits: Number(f.enrichment.credits) || 0,
          ...(f.enrichment.hasCustomPricing && {
            unitAmount: money.dollarsToCents(f.enrichment.dollars),
          }),
          level: f.enrichment.level || "PREMIUM",
          options: {
            hasListEnrichment: Boolean(f.enrichment.options?.hasListEnrichment),
            hasFreeListEnrichment: Boolean(f.enrichment.options?.hasFreeListEnrichment),
            hasEventIntelligence: Boolean(f.enrichment.options?.hasEventIntelligence),
          },
        },
      }),
      ...(f.dedicatedCsm
        && f.dedicatedCsm.enabled && {
        dedicatedCsm: {
          ...(f.dedicatedCsm.hasCustomPricing && {
            unitAmount: money.dollarsToCents(f.dedicatedCsm.dollars),
          }),
        },
      }),
      ...(f.billing?.contacts
        && Array.isArray(f.billing.contacts) && {
        meta: {
          billingContacts: (f.billing.contacts || [{ email: "", name: "" }])
            .filter((c) => c && c.email !== null && c.email !== undefined)
            .map(({ name, email }) => ({ name, email: String(email).trim() })),
        },
      }),
    };
  }, [form]);

  useEffect(() => {
    const controller = new AbortController();
    setIsLoadingPreview(true);
    const timeout = setTimeout(async () => {
      try {
        const preview = await previewSubscription(payload, controller.signal);
        setPreviewInfo(preview);
      } catch (err) {
        if (err.name !== "AbortError") {
          // do nothing
        }
      } finally {
        setIsLoadingPreview(false);
      }
    }, 1000);

    return () => {
      clearTimeout(timeout);
      controller.abort();
    };
  }, [
    payload.billing?.interval,
    payload.billing?.intervalCount,
    payload.seats,
    payload.enrichment,
    payload.dedicatedCsm,
    payload.billing?.trialExpiresOn,
    team?.id,
  ]);

  const onSubmit = async () => {
    setIsLoading(true);

    if (!team || !team.id) {
      setIsLoading(false);
      return;
    }

    try {
      const res = await createOrUpdateTeamSubscription(team.id, payload, isUpdateMode);
      if (res?.invoice) {
        setGeneratedInvoice(res.invoice);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const inputClasses = "mt-1.5 w-full border border-gray-300 rounded-lg px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all";
  const labelClasses = "block text-sm font-medium text-gray-900";
  const helperClasses = "text-xs text-gray-500 mt-1.5";

  const handleReset = () => {
    reset(initialForm);
  };

  const submitButtonText = useMemo(() => {
    if (team?.stripeSubscription) return "Update Subscription";
    if (form.billing?.trial?.enabled && internalDemoTeam) return "Update Trial";
    if (form.billing?.trial?.enabled && !internalDemoTeam) return "Create Trial";
    return "Create Subscription";
  }, [team?.stripeSubscription, form.billing?.trial?.enabled, internalDemoTeam]);

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <HeaderBar team={team} />

        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
              <div className="lg:col-span-2 space-y-6">
                <CollapsibleSection
                  title="Company Information"
                  isExpanded={sectionsExpanded.company}
                  onToggle={() => toggleSection("company")}
                >
                  <CompanyInfo
                    inputClasses={inputClasses}
                    labelClasses={labelClasses}
                    helperClasses={helperClasses}
                  />
                </CollapsibleSection>

                <CollapsibleSection
                  title="Billing Details"
                  isExpanded={sectionsExpanded.billing}
                  onToggle={() => toggleSection("billing")}
                >
                  <BillingDetails
                    isUpdateMode={isUpdateMode}
                    inputClasses={inputClasses}
                    labelClasses={labelClasses}
                    helperClasses={helperClasses}
                  />
                </CollapsibleSection>

                <CollapsibleSection
                  title="Enrichment Configuration"
                  isExpanded={sectionsExpanded.enrichment}
                  onToggle={() => toggleSection("enrichment")}
                  info={(() => {
                    const info = getPercentOffInfo(form?.enrichment?.percentOff);
                    return info ? <InfoBadge {...info} /> : null;
                  })()}
                >
                  <EnrichmentConfig
                    inputClasses={inputClasses}
                    labelClasses={labelClasses}
                    helperClasses={helperClasses}
                  />
                </CollapsibleSection>

                <CollapsibleSection
                  title="Seat Configuration"
                  isExpanded={sectionsExpanded.seats}
                  onToggle={() => toggleSection("seats")}
                  info={(() => {
                    const info = getPercentOffInfo(form?.seats?.percentOff);
                    return info ? <InfoBadge {...info} /> : null;
                  })()}
                >
                  <SeatsConfig
                    inputClasses={inputClasses}
                    labelClasses={labelClasses}
                    helperClasses={helperClasses}
                  />
                </CollapsibleSection>

                <CollapsibleSection
                  title="Dedicated CSM configuration"
                  isExpanded={sectionsExpanded.dedicatedCsm}
                  onToggle={() => toggleSection("dedicatedCsm")}
                  info={(() => {
                    const info = getPercentOffInfo(form?.dedicatedCsm?.percentOff);
                    return info ? <InfoBadge {...info} /> : null;
                  })()}
                >
                  <DedicatedCsmConfig inputClasses={inputClasses} />
                </CollapsibleSection>

                <ActionsBar
                  isLoading={isLoading}
                  onReset={handleReset}
                  onOpenStripePaymentModal={handleOpenStripePaymentModal}
                  onSubmitText={submitButtonText}
                  errors={errors}
                />
              </div>
              <div className="lg:col-span-1">
                <div className="sticky top-8 space-y-6">
                  <PreviewSidebar
                    isLoadingPreview={isLoadingPreview}
                    previewInfo={previewInfo}
                    isUpdateMode={isUpdateMode}
                  />
                  <StripeLinks
                    team={team}
                    internalDemoTeam={internalDemoTeam}
                    stripeCustomer={stripeCustomer}
                    invoice={generatedInvoice}
                  />
                </div>
              </div>
            </div>
          </form>
        </FormProvider>
      </div>
    </div>
  );
}
